import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, Link, UNSAFE_NavigationContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSmartBack } from "@/lib/navigation-utils";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowLeft,
  BadgeCheck,
  Cake,
  CircleUserRound,
  Copy,
  Dna,
  MapPin,
  Pencil,
  Phone,
  ShieldAlert,
  User,
  Weight,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@akuna/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle, SegmentedTabs } from "@akuna/ui";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import {
  MedicalRecordForm,
  type MedicalRecordFormHandle,
} from "@/components/medical-records/MedicalRecordForm";
import { useMedicalRecordForm } from "@/components/medical-records/form/use-medical-record-form";
import { MedicalRecordDocumentsSection } from "@/components/medical-records/MedicalRecordDocumentsSection";
import { RecentConsultationsCard } from "@/components/medical-records/RecentConsultationsCard";
import { AttendanceCarePanel } from "@/components/medical-records/AttendanceCarePanel";
import { PrescriptionComposer } from "@/components/prescriptions/PrescriptionComposer";
import { PrescriptionDraftProvider } from "@/components/prescriptions/editor/PrescriptionDraftContext";
import { PrescriptionDraftPreviewCard } from "@/components/prescriptions/editor/PrescriptionDraftPreviewCard";
import { useDraftContextData } from "@/components/prescriptions/editor/useDraftContextData";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { AddressLink } from "@/components/shared/AddressLink";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { invalidateReportsCache } from "@/lib/react-query/invalidateReportsCache";
import { ApplicationTab } from "@/components/medical-records/ApplicationTab";
import { PatientTermsPanel } from "@/components/terms/PatientTermsPanel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import { usePet } from "@/hooks/usePets";
import { useMedicalRecords } from "@/hooks/useMedicalRecords";
import { useExamAttachments, type ExamAttachmentUpload } from "@/hooks/useExamAttachments";
import { useProfile } from "@/hooks/useProfile";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import type { MedicalRecordFormInput, MedicalRecordInput } from "@/lib/validations/medical-record";
import { toast } from "sonner";
import { useTranscription } from "@/contexts/TranscriptionContext";
import { formatDateBR, formatDateTimeBR, parseISODateOrDateTime } from "@/lib/date-utils";
import { ProfileDataModal } from "@/components/prescriptions/ProfileDataModal";
import { QuickEditPetDialog } from "@/components/pets/QuickEditPetDialog";
import { QuickEditOwnerDialog } from "@/components/owners/QuickEditOwnerDialog";
import { MobilePatientEditFlow } from "@/components/medical-records/mobile/MobilePatientEditFlow";
import { createOrUpdateFollowUpAppointment } from "@/lib/utils/follow-up-appointment";
import { generateUUID } from "@/lib/utils/uuid";
import { getLatestPetWeight } from "@/lib/utils/pet-weight";
import {
  ReturnApplicationConflictDialog,
  type ReturnApplicationConflictChoice,
  type ApplicationAppointmentSummary,
} from "@/components/medical-records/ReturnApplicationConflictDialog";
import { getLocalDayBounds } from "@/lib/utils/next-dose";
import {
  clearAttendanceDraft,
  readAttendanceDraft,
  type AttendanceSource,
} from "@/lib/attendance/session-storage";
import {
  applyPetDeath,
  cancelFutureAppointmentsForPet,
  fetchFutureAppointmentsForPet,
  isPetDeceased,
} from "@/lib/utils/pet-death";
import { resolveFinishRedirect } from "@/pages/medical-records/resolveFinishRedirect";
import {
  isMedicalRecordInternalTabNavigation,
  resolveNavigationPathname,
} from "@/lib/medical-record-internal-tab-navigation";
import { MedicalRecordMobileLayout } from "@/components/medical-records/mobile/MedicalRecordMobileLayout";
import { MobileMedicalRecordHeader } from "@/components/medical-records/mobile/MobileMedicalRecordHeader";
import { MobilePatientChip } from "@/components/medical-records/mobile/MobilePatientChip";
import { MobileMedicalRecordTabs } from "@/components/medical-records/mobile/MobileMedicalRecordTabs";
import { MobileMedicalRecordActionBar } from "@/components/medical-records/mobile/MobileMedicalRecordActionBar";
import { MobilePatientContextSheet } from "@/components/medical-records/mobile/MobilePatientContextSheet";
import { buildMedicalRecordPatientViewModel } from "@/components/medical-records/mobile/medical-record-patient-view-model";
import { MobileRecordingBar } from "@/components/medical-records/mobile/MobileRecordingBar";
import { formatDuration } from "@/components/medical-records/transcription/utils";
import { resolveMedicalRecordMobileConfig } from "@/components/medical-records/mobile/medical-record-mobile-config";
import type { MedicalRecordPrimaryTab } from "@/components/medical-records/mobile/medical-record-mobile.types";

const formatDate = (value?: string | null) => {
  return formatDateBR(value);
};

type CreateMedicalRecordLocationState = {
  appointmentId?: string;
  fromCalendar?: boolean;
  quickStartAttendance?: boolean;
  quickStartSource?: AttendanceSource;
};


type PetConditionEntry = {
  id: string;
  condition?: { id: string; name: string; type: string } | null;
  name?: string | null;
};

const resolvePhotoPath = (
  photo?: { file_path?: string } | { file_path?: string }[] | null,
): string | null => {
  if (!photo) return null;
  if (Array.isArray(photo)) return photo[0]?.file_path ?? null;
  return photo.file_path ?? null;
};

type MedicalRecordRouteTab = "record" | "exams" | "prescriptions" | "applications" | "terms";

const MEDICAL_RECORD_TAB_BY_ROUTE: Record<string, MedicalRecordRouteTab> = {
  record: "record",
  exams: "exams",
  prescriptions: "prescriptions",
  applications: "applications",
  terms: "terms",
  novo: "record",
  exames: "exams",
  prescricao: "prescriptions",
  vacinas: "applications",
  termos: "terms",
};

const ROUTE_TAB_TO_LEGACY: Record<MedicalRecordRouteTab, "novo" | "exames" | "prescricao" | "vacinas" | "termos"> = {
  record: "novo",
  exams: "exames",
  prescriptions: "prescricao",
  applications: "vacinas",
  terms: "termos",
};

const CreateMedicalRecord = () => {
  const { t, i18n } = useTranslation("medicalRecords");
  const navigate = useNavigate();
  const smartBack = useSmartBack();
  const { id, recordTab } = useParams<{ id: string; recordTab?: string }>();
  const location = useLocation();
  const { appointmentId, fromCalendar, quickStartAttendance, quickStartSource } =
    (location.state as CreateMedicalRecordLocationState | null) ?? {};
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobile();
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const routeTab = useMemo<MedicalRecordRouteTab | null>(() => {
    if (!recordTab) return null;
    return MEDICAL_RECORD_TAB_BY_ROUTE[recordTab] ?? null;
  }, [recordTab]);
  const activeTab: "novo" | "exames" | "prescricao" | "vacinas" | "termos" = ROUTE_TAB_TO_LEGACY[routeTab ?? "record"];
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [draftVaccinationIds, setDraftVaccinationIds] = useState<string[]>([]);
  const [draftPrescriptionIds, setDraftPrescriptionIds] = useState<string[]>([]);
  const [draftTermIds, setDraftTermIds] = useState<string[]>([]);
  const [showProfileDataModal, setShowProfileDataModal] = useState(false);
  const [isEditPetOpen, setIsEditPetOpen] = useState(false);
  const [isEditOwnerOpen, setIsEditOwnerOpen] = useState(false);
  const [isMobileEditOpen, setIsMobileEditOpen] = useState(false);
  const [isMobileContextOpen, setIsMobileContextOpen] = useState(false);
  const [pendingMedicalRecord, setPendingMedicalRecord] = useState<MedicalRecordFormInput | null>(null);
  const [returnVaccineConflictOpen, setReturnVaccineConflictOpen] = useState(false);
  const [returnVaccineConflictChoice, setReturnVaccineConflictChoice] = useState<ReturnApplicationConflictChoice | null>(null);
  const [returnVaccineConflictContext, setReturnVaccineConflictContext] = useState<{
    followUpDate: Date;
    vaccineAppointments: ApplicationAppointmentSummary[];
  } | null>(null);
  const returnVaccineConflictResolverRef = useRef<((value: ReturnApplicationConflictChoice | null) => void) | null>(null);
  const formRef = useRef<MedicalRecordFormHandle>(null);
  const pendingNavigationActionRef = useRef<(() => void) | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);
  const isProcessingBlockRef = useRef(false);
  const allowNextNavigationRef = useRef(false);
  const skipUnsavedCheckRef = useRef(false);
  const locationPathnameRef = useRef(location.pathname);
  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);
  const {
    open: openTranscription,
    state: transcriptionState,
    discardSession: discardTranscriptionSession,
    isOpen: isTranscriptionOpen,
    isMinimized: isTranscriptionMinimized,
    session: transcriptionSession,
    recordingPhase,
    recordingDuration,
    maximize: maximizeTranscription,
    pauseRecording,
    resumeRecording,
    stopRecording,
    retryAiExtraction,
    reconnect: reconnectTranscription,
  } = useTranscription();

  const queryClient = useQueryClient();
  const { data: pet, isLoading: isLoadingPet, error } = usePet(id ?? "");
  const {
    medicalRecords,
    isLoading: isLoadingMedicalRecords,
    createMedicalRecord,
    deleteMedicalRecord,
  } = useMedicalRecords(id);
  const { data: profile, isLoading: isLoadingProfile } = useProfile();
  const draftContext = useDraftContextData({ pet, medicalRecordId: null });

  useEffect(() => {
    if (routeTab) return;
    if (!id) return;
    navigate(`/patients/pets/${id}/medical-records/new/record`, { replace: true, state: location.state });
  }, [id, location.state, navigate, routeTab]);

  const clinicId = profile?.current_clinic_id ?? "";
  const normalizedClinicId = clinicId || null;
  const hasCRMV = Boolean(
    profile?.crmv_state &&
    profile.crmv_state.trim() !== "" &&
    profile?.crmv_number &&
    profile.crmv_number > 0
  );

  const attachmentDraftStorageKey = useMemo(() => {
    if (!pet?.id || !clinicId) return null;
    return `medical-record-create:attachmentsDraftToken:${clinicId}:${pet.id}:${appointmentId ?? "none"}`;
  }, [appointmentId, clinicId, pet?.id]);

  const attachmentDraftTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!attachmentDraftStorageKey) return;
    if (attachmentDraftTokenRef.current) return;
    if (typeof window === "undefined") return;

    try {
      const existing = window.localStorage.getItem(attachmentDraftStorageKey);
      if (existing && existing.trim() !== "") {
        attachmentDraftTokenRef.current = existing;
        return;
      }
      const created = generateUUID();
      window.localStorage.setItem(attachmentDraftStorageKey, created);
      attachmentDraftTokenRef.current = created;
    } catch (err) {
      console.warn("Não foi possível inicializar token de anexos:", err);
      attachmentDraftTokenRef.current = generateUUID();
    }
  }, [attachmentDraftStorageKey]);

  const {
    attachments: examAttachments,
    isLoading: isLoadingAttachments,
    uploadAttachments,
    deleteAttachment,
    updateAttachment,
  } = useExamAttachments(pet?.id, undefined, normalizedClinicId);

  const handleUploadExamAttachments = useCallback(async (items: ExamAttachmentUpload[]) => {
    const token = attachmentDraftTokenRef.current;
    if (!token) {
      await uploadAttachments(items);
      return;
    }

    const draftTag = `draft:${token}`;
    const sourceTag = "source:create-medical-record";

    const tagged = items.map((item) => {
      const tags = [...(item.tags ?? [])];
      if (!tags.includes(draftTag)) tags.push(draftTag);
      if (!tags.includes(sourceTag)) tags.push(sourceTag);
      return { ...item, tags };
    });

    await uploadAttachments(tagged);
  }, [uploadAttachments]);

  const age = useMemo(() => {
    if (!pet?.birth_date) return null;
    const birthDate = parseISODateOrDateTime(pet.birth_date);
    if (!birthDate) return null;
    return formatDistanceToNow(birthDate, {
      addSuffix: false,
      locale: ptBR,
    });
  }, [pet?.birth_date]);

  const lastWeight = useMemo(() => {
    return getLatestPetWeight((pet?.weights ?? []) as Array<{ weight: number; measured_at?: string | null; created_at?: string | null; deleted_at?: string | null }>);
  }, [pet?.weights]);

  // Check if there's unsaved data on the medical record form
  const hasUnsavedData = useCallback(async (): Promise<boolean> => {
    if (skipUnsavedCheckRef.current) {
      skipUnsavedCheckRef.current = false;
      return false;
    }

    const isFormDirty = formRef.current?.isDirty?.() ?? false;
    return isFormDirty || hasFormChanges || transcriptionState !== "closed";
  }, [hasFormChanges, transcriptionState]);

  // Handle navigation with confirmation
  const handleNavigation = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const navigationContext = useContext(UNSAFE_NavigationContext);
  const navigatorInstance = navigationContext?.navigator;

  // Delete draft medical record and transcriptions
  const deleteDraftData = useCallback(async () => {
    try {
      // Delete transcriptions for this pet without medical_record_id
      if (pet?.id) {
        await supabase
          .from("consultation_transcriptions")
          .delete()
          .eq("pet_id", pet.id)
          .is("medical_record_id", null);
      }
    } catch (deleteError) {
      console.error("Erro ao deletar dados do rascunho:", deleteError);
    }
  }, [pet?.id]);

  const attemptNavigation = useCallback(
    async (execute: () => void) => {
      if (isProcessingBlockRef.current) {
        return;
      }

      isProcessingBlockRef.current = true;
      try {
        const hasData = await hasUnsavedData();
        if (hasData) {
          pendingNavigationActionRef.current = execute;
          setIsExitDialogOpen(true);
        } else {
          allowNextNavigationRef.current = true;
          execute();
        }
      } finally {
        isProcessingBlockRef.current = false;
      }
    },
    [hasUnsavedData],
  );

  useEffect(() => {
    discardTranscriptionSession();
    // The page must clear any leftover transcription only on entry/exit.
    // Re-running on action identity changes causes a discard loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      discardTranscriptionSession();
    };
    // See mount-only cleanup note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!navigatorInstance) return;

    const nav = navigatorInstance as unknown as {
      push?: (...args: unknown[]) => void;
      replace?: (...args: unknown[]) => void;
      go?: (...args: unknown[]) => void;
    };

    const cleanupCallbacks: Array<() => void> = [];

    if (typeof nav.push === "function") {
      const originalPush = nav.push;
      nav.push = ((...args: unknown[]) => {
        if (allowNextNavigationRef.current) {
          allowNextNavigationRef.current = false;
          originalPush.apply(navigatorInstance, args as unknown[]);
          return;
        }
        const nextPath = resolveNavigationPathname(args[0], locationPathnameRef.current);
        if (nextPath && isMedicalRecordInternalTabNavigation(locationPathnameRef.current, nextPath)) {
          originalPush.apply(navigatorInstance, args as unknown[]);
          return;
        }
        void attemptNavigation(() => {
          originalPush.apply(navigatorInstance, args as unknown[]);
        });
      }) as typeof nav.push;
      cleanupCallbacks.push(() => {
        nav.push = originalPush;
      });
    }

    if (typeof nav.replace === "function") {
      const originalReplace = nav.replace;
      nav.replace = ((...args: unknown[]) => {
        if (allowNextNavigationRef.current) {
          allowNextNavigationRef.current = false;
          originalReplace.apply(navigatorInstance, args as unknown[]);
          return;
        }
        const nextPath = resolveNavigationPathname(args[0], locationPathnameRef.current);
        if (nextPath && isMedicalRecordInternalTabNavigation(locationPathnameRef.current, nextPath)) {
          originalReplace.apply(navigatorInstance, args as unknown[]);
          return;
        }
        void attemptNavigation(() => {
          originalReplace.apply(navigatorInstance, args as unknown[]);
        });
      }) as typeof nav.replace;
      cleanupCallbacks.push(() => {
        nav.replace = originalReplace;
      });
    }

    if (typeof nav.go === "function") {
      const originalGo = nav.go;
      nav.go = ((...args: unknown[]) => {
        if (allowNextNavigationRef.current) {
          allowNextNavigationRef.current = false;
          originalGo.apply(navigatorInstance, args as unknown[]);
          return;
        }
        void attemptNavigation(() => {
          originalGo.apply(navigatorInstance, args as unknown[]);
        });
      }) as typeof nav.go;
      cleanupCallbacks.push(() => {
        nav.go = originalGo;
      });
    }

    if (cleanupCallbacks.length === 0) {
      return;
    }

    unblockRef.current = () => {
      cleanupCallbacks.forEach((cb) => cb());
      unblockRef.current = null;
    };

    return () => {
      unblockRef.current?.();
    };
  }, [attemptNavigation, navigatorInstance]);

  const handleConfirmExit = useCallback(async () => {
    await deleteDraftData();
    discardTranscriptionSession();
    setIsExitDialogOpen(false);
    const action = pendingNavigationActionRef.current;
    pendingNavigationActionRef.current = null;
    setHasFormChanges(false);
    skipUnsavedCheckRef.current = true;
    allowNextNavigationRef.current = true;

    if (action) {
      action();
    }
  }, [deleteDraftData, discardTranscriptionSession]);

  const handleCancelExit = useCallback(() => {
    pendingNavigationActionRef.current = null;
    setIsExitDialogOpen(false);
  }, []);

  // Intercept back button
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const isFormDirty = formRef.current?.isDirty?.() ?? false;
      if (isFormDirty || hasFormChanges || transcriptionState !== "closed") {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasFormChanges, transcriptionState]);

  const normalizeStructuredData = (values: Partial<MedicalRecordInput>): Partial<MedicalRecordInput> => {
    const normalized: Partial<MedicalRecordInput> = { ...values };

    if (typeof values.consultation_date === "string") {
      const parsed = new Date(values.consultation_date);
      if (!Number.isNaN(parsed.getTime())) {
        normalized.consultation_date = parsed;
      }
    }

    if (typeof values.follow_up_date === "string") {
      const parsed = new Date(values.follow_up_date);
      if (!Number.isNaN(parsed.getTime())) {
        normalized.follow_up_date = parsed;
      }
    }

    type NumericFieldKey = "weight" | "temperature" | "heart_rate" | "respiratory_rate";

    const numericFields: NumericFieldKey[] = [
      "weight",
      "temperature",
      "heart_rate",
      "respiratory_rate",
    ];

    numericFields.forEach((field) => {
      const value = values[field] as string | number | undefined;
      if (typeof value === "string" && value.trim().length > 0) {
        const parsed = Number(value.replace(",", "."));
        if (!Number.isNaN(parsed)) {
          const numericValue =
            field === "heart_rate" || field === "respiratory_rate" ? Math.round(parsed) : parsed;
          (normalized as Partial<Record<NumericFieldKey, number>>)[field] = numericValue;
        }
      }
    });

    return normalized;
  };

  const promptReturnVaccineConflictResolution = useCallback(
    (ctx: { followUpDate: Date; vaccineAppointments: ApplicationAppointmentSummary[] }) =>
      new Promise<ReturnApplicationConflictChoice | null>((resolve) => {
        returnVaccineConflictResolverRef.current = resolve;
        setReturnVaccineConflictContext(ctx);
        setReturnVaccineConflictChoice({ keepFollowUp: true, keepVaccines: true });
        setReturnVaccineConflictOpen(true);
      }),
    [],
  );

  const resolveReturnVaccineConflict = useCallback((value: ReturnApplicationConflictChoice | null) => {
    const resolver = returnVaccineConflictResolverRef.current;
    returnVaccineConflictResolverRef.current = null;
    setReturnVaccineConflictOpen(false);
    resolver?.(value);
  }, []);

  const enrichChiefComplaintWithSummary = (
    chiefComplaint?: string | null,
    summary?: string | null,
  ) => {
    const trimmedChiefComplaint = chiefComplaint?.trim() ?? "";
    const trimmedSummary = summary?.trim() ?? "";

    if (trimmedChiefComplaint && trimmedSummary) {
      return `${trimmedChiefComplaint}\n\n${trimmedSummary}`;
    }

    return trimmedChiefComplaint || trimmedSummary || "";
  };

  const handleApplyTranscription = (
    data: Partial<MedicalRecordInput>,
    _transcript: string,
    _summary?: string | null,
  ) => {
    const normalized = normalizeStructuredData(data);
    delete normalized.consultation_date;

    formRef.current?.applyValues(normalized);
    toast.success(t("create.autoFillSuccess"));
  };

  const handleCopyRecordText = useCallback(async () => {
    if (!formRef.current) {
      toast.error(t("actions.copyRecordUnavailable"));
      return;
    }
    const text = formRef.current.getClipboardText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("actions.copyRecordSuccess"));
    } catch {
      toast.error(t("actions.copyRecordError"));
    }
  }, [t]);

  const formController = useMedicalRecordForm({
    petId: pet?.id ?? id ?? "",
    appointmentId,
    clinicId,
    onSubmit: async (data) => handleSubmit(data),
    onDirtyChange: setHasFormChanges,
  });

  if (!id) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">{t("notFound")}</h1>
        </div>
      </div>
    );
  }

  if (isLoadingPet || isLoadingProfile) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-56" />
        </div>
        <Skeleton className="h-20 w-full" />
        <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
          <Skeleton className="h-[640px] w-full" />
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !pet) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">{t("loadErrorTitle")}</h1>
        </div>
        <Card>
          <CardContent className="py-6">
            <p className="text-muted-foreground">
              {t("loadErrorDescription")}
            </p>
            <Button className="mt-4" onClick={() => smartBack()}>
              {t("backToPatients")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const goBackToPet = () => {
    if (fromCalendar) {
      smartBack("/calendar");
    } else {
      smartBack(`/patients/pets/${pet.id}/history`);
    }
  };

  const handleGoBackToPet = () => {
    void attemptNavigation(goBackToPet);
  };
  const ownerPrimaryPhone =
    pet.owner?.phones?.find((phone) => phone.is_primary) ?? pet.owner?.phones?.[0] ?? null;
  const ownerPrimaryPhoneLabel = ownerPrimaryPhone?.phone_number?.trim()
    ? ownerPrimaryPhone.phone_number
    : null;
  const ownerPrimaryAddress =
    pet.owner?.addresses?.find((address) => address.is_primary) ?? pet.owner?.addresses?.[0] ?? null;
  const ownerPrimaryAddressLabel = ownerPrimaryAddress
    ? [
      ownerPrimaryAddress.street,
      ownerPrimaryAddress.number,
      ownerPrimaryAddress.complement,
      ownerPrimaryAddress.neighborhood,
      ownerPrimaryAddress.city?.name,
      ownerPrimaryAddress.city?.state?.name,
      ownerPrimaryAddress.zip_code,
    ].filter(Boolean).join(", ")
    : null;
  const petPhoto = resolvePhotoPath(pet.photo ?? null);
  const lastWeightSummary = lastWeight?.weight != null
    ? `${lastWeight.weight} kg${lastWeight.measured_at ? ` (${formatDate(lastWeight.measured_at)})` : ""}`
    : null;
  const sterilizationName = pet.sterilization?.name?.trim().toLowerCase() ?? "";
  const sterilizationLabel = sterilizationName
    ? sterilizationName.includes("não") || sterilizationName.includes("nao")
      ? "Não castrado"
      : "Castrado"
    : null;
  const petSummaryItems: Array<{ key: string; Icon: LucideIcon; label: string }> = [];
  if (pet.gender?.name) petSummaryItems.push({ key: "gender", Icon: CircleUserRound, label: pet.gender.name });
  if (pet.breed?.name) petSummaryItems.push({ key: "breed", Icon: Dna, label: pet.breed.name });
  if (age) petSummaryItems.push({ key: "age", Icon: Cake, label: age });
  if (sterilizationLabel) petSummaryItems.push({ key: "sterilization", Icon: BadgeCheck, label: sterilizationLabel });
  if (lastWeightSummary) petSummaryItems.push({ key: "weight", Icon: Weight, label: lastWeightSummary });
  const petAllergies = (pet.allergies ?? []) as PetConditionEntry[];
  const petTemperaments = (pet.temperaments ?? []) as PetConditionEntry[];

  const handleCreateMedicalRecord = async (data: MedicalRecordFormInput) => {
    try {
      setIsSubmitting(true);
      const { data: authData } = await supabase.auth.getUser();
      const currentUserId = authData.user?.id ?? null;
      const isQuickStartFlow = Boolean(quickStartAttendance);
      const activeAttendanceDraft =
        isQuickStartFlow && currentUserId
          ? readAttendanceDraft(clinicId, currentUserId)
          : null;

      const resolvedAppointmentId =
        data.appointment_id?.trim() ||
        appointmentId ||
        activeAttendanceDraft?.appointmentId ||
        null;

      // Create medical record
      const newRecord = await createMedicalRecord({
        pet_id: pet.id,
        appointment_id: resolvedAppointmentId,
        clinic_id: clinicId,
        consultation_date: data.consultation_date.toISOString(),
        chief_complaint: data.chief_complaint,
        history: data.history || null,
        behavior: data.behavior || null,
        diet: data.diet || null,
        environment: data.environment || null,
        weight: data.weight || null,
        temperature: data.temperature || null,
        heart_rate: data.heart_rate || null,
        respiratory_rate: data.respiratory_rate || null,
        mucous_membranes: data.mucous_membranes || null,
        capillary_refill_time: data.capillary_refill_time || null,
        hydration_status: data.hydration_status || null,
        lymph_nodes: data.lymph_nodes || null,
        cardiac_auscultation: data.cardiac_auscultation || null,
        respiratory_auscultation: data.respiratory_auscultation || null,
        abdominal_palpation: data.abdominal_palpation || null,
        locomotor_system: data.locomotor_system || null,
        skin_coat: data.skin_coat || null,
        other_systems: data.other_systems || null,
        presumptive_diagnosis: data.presumptive_diagnosis || null,
        definitive_diagnosis: data.definitive_diagnosis || null,
        treatment: data.treatment || null,
        follow_up_date: data.follow_up_date
          ? data.follow_up_date.toISOString()
          : null,
        follow_up_notes: data.follow_up_notes || null,
      });

      if (!newRecord?.id) throw new Error("Falha ao criar consulta");

      if (data.register_death && data.death_date) {
        await applyPetDeath({
          petId: pet.id,
          deathDate: data.death_date,
          deathCause: data.death_cause,
          ownerId: pet.owner?.id,
          queryClient,
        });

        const futureAppointments = await fetchFutureAppointmentsForPet(pet.id);
        if (futureAppointments.length > 0) {
          const shouldCancel = window.confirm(
            t("death.confirmFutureAppointments", { count: futureAppointments.length }),
          );
          if (shouldCancel) {
            await cancelFutureAppointmentsForPet(pet.id);
            queryClient.invalidateQueries({ queryKey: ["appointments"] });
          }
        }
      }

      if (isQuickStartFlow && resolvedAppointmentId) {
        const startedAt = activeAttendanceDraft
          ? new Date(activeAttendanceDraft.startedAtIso)
          : null;
        const now = new Date();
        const safeEndAt = startedAt && !Number.isNaN(startedAt.getTime()) && now.getTime() <= startedAt.getTime()
          ? new Date(startedAt.getTime() + 60_000)
          : now;

        const { error: finalizeError } = await supabase
          .from("appointments")
          .update({
            status: "finalizado",
            ends_at: safeEndAt.toISOString(),
          })
          .eq("id", resolvedAppointmentId);

        if (finalizeError) throw finalizeError;

        if (!data.appointment_id?.trim() && !appointmentId) {
          const { error: linkAppointmentError } = await supabase
            .from("medical_records")
            .update({ appointment_id: resolvedAppointmentId })
            .eq("id", newRecord.id);

          if (linkAppointmentError) {
            console.warn("Consulta criada, mas falhou ao vincular agendamento rápido:", linkAppointmentError);
          }
        }

        queryClient.invalidateQueries({ queryKey: ["appointments"] });
        invalidateReportsCache(queryClient);
      }

      if (isQuickStartFlow && currentUserId && resolvedAppointmentId) {
        clearAttendanceDraft(clinicId, currentUserId);
      }

      // Link exam attachments uploaded on this screen (marked via draft token)
      const attachmentDraftToken = attachmentDraftTokenRef.current;
      if (attachmentDraftToken) {
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData.user?.id ?? null;

        let linkQuery = supabase
          .from("pet_attachments")
          .update({ medical_record_id: newRecord.id })
          .eq("pet_id", pet.id)
          .eq("clinic_id", clinicId)
          .is("medical_record_id", null)
          .is("deleted_at", null)
          .contains("tags", [`draft:${attachmentDraftToken}`]);

        if (userId) {
          linkQuery = linkQuery.eq("created_by", userId);
        }

        const { error: linkAttachmentsError } = await linkQuery;
        if (linkAttachmentsError) {
          console.warn("Erro ao vincular anexos de exames deste prontuário:", linkAttachmentsError);
        } else if (attachmentDraftStorageKey && typeof window !== "undefined") {
          try {
            window.localStorage.removeItem(attachmentDraftStorageKey);
          } catch (err) {
            console.warn("Não foi possível limpar token de anexos:", err);
          }
          attachmentDraftTokenRef.current = null;
        }

        queryClient.invalidateQueries({ queryKey: ["pet-attachments", pet.id] });
      }

      let linkedVaccinationsCount = 0;
      let linkedPrescriptionsCount = 0;
      let linkedTermsCount = 0;
      let linkageHadErrors = false;

      // Link items created on this screen (without heuristics / time windows).
      if (draftVaccinationIds.length > 0) {
        const { data: linkedVaccinations, error: linkError } = await supabase
          .from("pet_applications")
          .update({ medical_record_id: newRecord.id })
          .in("id", draftVaccinationIds)
          .eq("pet_id", pet.id)
          .eq("clinic_id", clinicId)
          .is("medical_record_id", null)
          .is("deleted_at", null)
          .select("id");

        if (linkError) {
          linkageHadErrors = true;
          console.warn("Erro ao vincular vacinações desta consulta:", linkError);
        } else {
          linkedVaccinationsCount = linkedVaccinations?.length ?? 0;
          queryClient.invalidateQueries({ queryKey: ["pet-vaccinations", pet.id] });
          queryClient.invalidateQueries({ queryKey: ["application-stats"] });
          queryClient.invalidateQueries({ queryKey: ["appointments"] });
          invalidateReportsCache(queryClient);
        }
      }

      if (draftPrescriptionIds.length > 0) {
        const { data: linkedPrescriptions, error: linkError } = await supabase
          .from("medical_prescriptions")
          .update({ medical_record_id: newRecord.id })
          .in("id", draftPrescriptionIds)
          .eq("pet_id", pet.id)
          .eq("clinic_id", clinicId)
          .is("medical_record_id", null)
          .is("deleted_at", null)
          .select("id");

        if (linkError) {
          linkageHadErrors = true;
          console.warn("Erro ao vincular receitas desta consulta:", linkError);
        } else {
          linkedPrescriptionsCount = linkedPrescriptions?.length ?? 0;
          queryClient.invalidateQueries({ queryKey: ["medical-prescriptions", pet.id] });
        }
      }

      if (draftTermIds.length > 0) {
        const { data: linkedTerms, error: linkError } = await supabase
          .from("patient_terms")
          .update({ medical_record_id: newRecord.id })
          .in("id", draftTermIds)
          .eq("pet_id", pet.id)
          .eq("clinic_id", clinicId)
          .is("medical_record_id", null)
          .is("deleted_at", null)
          .select("id");

        if (linkError) {
          linkageHadErrors = true;
          console.warn("Erro ao vincular termos desta consulta:", linkError);
        } else {
          linkedTermsCount = linkedTerms?.length ?? 0;
          queryClient.invalidateQueries({ queryKey: ["patient-terms", pet.id] });
        }
      }

      setDraftVaccinationIds([]);
      setDraftPrescriptionIds([]);
      setDraftTermIds([]);

      // Link any existing consultation transcriptions to this medical record
      const { error: updateError } = await supabase
        .from("consultation_transcriptions")
        .update({ medical_record_id: newRecord.id })
        .eq("pet_id", pet.id)
        .is("medical_record_id", null);

      if (updateError) {
        console.error("Erro ao vincular transcrição:", updateError);
      }

      // Criar ou atualizar agendamento de retorno apenas quando solicitado explicitamente
      if (data.follow_up_date && data.create_follow_up_appointment) {
        if (!clinicId) {
          console.warn("Não foi possível agendar retorno: clínica não definida.");
        } else if (!pet.owner?.id) {
          console.warn("Não foi possível agendar retorno: responsável sem identificador.");
        } else {
          try {
            const { start, end } = getLocalDayBounds(data.follow_up_date);
            const { data: vaccineAppointmentsRaw, error: vaccineAppointmentsError } = await supabase
              .from("appointments")
              .select("id, starts_at")
              .eq("clinic_id", clinicId)
              .eq("type", "vacina")
              .gte("starts_at", start.toISOString())
              .lte("starts_at", end.toISOString())
              .in("status", ["agendado", "confirmado"])
              .is("deleted_at", null)
              .limit(20);

            if (vaccineAppointmentsError) {
              console.warn("Não foi possível verificar conflito de agendamentos de vacina:", vaccineAppointmentsError);
            }

            const vaccineAppointmentsCandidates = (vaccineAppointmentsRaw ?? []) as ApplicationAppointmentSummary[];
            let vaccineAppointmentsForPet: ApplicationAppointmentSummary[] = [];

            if (vaccineAppointmentsCandidates.length > 0) {
              const ids = vaccineAppointmentsCandidates.map((a) => a.id);
              const { data: relations, error: relationsError } = await supabase
                .from("appointment_pets")
                .select("appointment_id")
                .eq("pet_id", pet.id)
                .in("appointment_id", ids);

              if (relationsError) {
                console.warn("Não foi possível verificar vínculo do paciente nos agendamentos de vacina:", relationsError);
              } else {
                const allowed = new Set((relations ?? []).map((r) => r.appointment_id));
                vaccineAppointmentsForPet = vaccineAppointmentsCandidates.filter((a) => allowed.has(a.id));
              }
            }

            let conflictChoice: ReturnApplicationConflictChoice | null = null;
            if (vaccineAppointmentsForPet.length > 0) {
              conflictChoice = await promptReturnVaccineConflictResolution({
                followUpDate: data.follow_up_date,
                vaccineAppointments: vaccineAppointmentsForPet,
              });

              if (conflictChoice && !conflictChoice.keepVaccines) {
                const ids = vaccineAppointmentsForPet.map((a) => a.id);
                let hadDeleteError = false;
                for (const appointmentId of ids) {
                  const { error } = await supabase.rpc("soft_delete_appointment", {
                    p_appointment_id: appointmentId,
                  });
                  if (error) {
                    hadDeleteError = true;
                    console.warn("Não foi possível remover agendamento(s) de vacina:", error);
                  }
                }
                if (!hadDeleteError) {
                  queryClient.invalidateQueries({ queryKey: ["appointments"] });
                  invalidateReportsCache(queryClient);
                }

                const { error: unlinkError } = await supabase
                  .from("pet_applications")
                  .update({ appointment_id: null })
                  .eq("pet_id", pet.id)
                  .in("appointment_id", ids);
                if (unlinkError) {
                  console.warn("Não foi possível desvincular agendamento(s) das vacinações:", unlinkError);
                }
              }

              if (conflictChoice && !conflictChoice.keepFollowUp) {
                const { error: clearError } = await supabase
                  .from("medical_records")
                  .update({ follow_up_date: null, follow_up_notes: null })
                  .eq("id", newRecord.id);

                if (clearError) {
                  console.warn("Não foi possível remover data de retorno do prontuário:", clearError);
                }
              }
            }

            const shouldScheduleReturn = conflictChoice ? conflictChoice.keepFollowUp : true;
            const appointmentId = shouldScheduleReturn
              ? await createOrUpdateFollowUpAppointment({
                medicalRecordId: newRecord.id,
                petId: pet.id,
                ownerId: pet.owner.id,
                clinicId: clinicId,
                vetUserId: newRecord.vet_user_id ?? null,
                followUpDate: data.follow_up_date,
                followUpNotes: data.follow_up_notes || null,
              })
              : null;

            if (appointmentId) {
              toast.success(t("create.followUpScheduled"));
              // Invalidar queries para atualizar a lista de agendamentos
              queryClient.invalidateQueries({ queryKey: ["appointments"] });
              invalidateReportsCache(queryClient);
            }
          } catch (appointmentError) {
            console.error("Erro ao agendar retorno:", appointmentError);
            toast.error(t("create.followUpScheduleError"));
          }
        }
      }

      if (linkageHadErrors) {
        toast.warning(t("create.createdLinkWarning"));
      } else if (linkedVaccinationsCount > 0 || linkedPrescriptionsCount > 0 || linkedTermsCount > 0) {
        const pieces = [
          linkedVaccinationsCount > 0 ? t("links.vaccines", { count: linkedVaccinationsCount }) : null,
          linkedPrescriptionsCount > 0 ? t("links.prescriptions", { count: linkedPrescriptionsCount }) : null,
          linkedTermsCount > 0 ? t("links.terms", { count: linkedTermsCount }) : null,
        ].filter(Boolean);
        toast.success(t("create.createdAndLinked", { items: pieces.join(" + ") }));
      } else {
        toast.success(t("create.createdSuccess"));
      }

      formRef.current?.reset(data);
      setHasFormChanges(false);
      discardTranscriptionSession();
      if (pet?.id) {
        skipUnsavedCheckRef.current = true;
        const redirect = resolveFinishRedirect({
          isQuickStartFlow,
          quickStartSource,
          fromCalendar,
          petId: pet.id,
          appointmentId: resolvedAppointmentId,
        });
        if ("smartBackTo" in redirect) {
          smartBack(redirect.smartBackTo);
        } else {
          navigate(redirect.to, { state: redirect.state });
        }
      }
    } catch (submitError) {
      console.error("Erro ao criar consulta:", submitError);
      toast.error(t("create.createError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (data: MedicalRecordFormInput) => {
    if (!hasCRMV) {
      setPendingMedicalRecord(data);
      setShowProfileDataModal(true);
      return;
    }

    await handleCreateMedicalRecord(data);
  };

  const handleProfileDataComplete = async () => {
    setShowProfileDataModal(false);
    await queryClient.refetchQueries({ queryKey: ["profile"] });
    const updatedProfile = queryClient.getQueryData<{
      crmv_state?: string | null;
      crmv_number?: number | null;
    }>(["profile"]);
    const hasCrmv = Boolean(
      updatedProfile?.crmv_state &&
      updatedProfile.crmv_state.trim() !== "" &&
      updatedProfile?.crmv_number &&
      updatedProfile.crmv_number > 0
    );
    const pendingRecord = pendingMedicalRecord;

    if (pendingRecord && hasCrmv) {
      setPendingMedicalRecord(null);
      await handleCreateMedicalRecord(pendingRecord);
    } else {
      setPendingMedicalRecord(null);
    }
  };

  const mobileActiveTab: MedicalRecordPrimaryTab = {
    novo: "record",
    exames: "documents",
    prescricao: "prescriptions",
    vacinas: "applications",
    termos: "terms",
  }[activeTab] as MedicalRecordPrimaryTab;
  const mobileConfig = resolveMedicalRecordMobileConfig({ mode: "create", canEdit: true });
  const mobilePatient = buildMedicalRecordPatientViewModel({
    pet,
    age,
    lastWeight,
    locale: i18n.resolvedLanguage ?? i18n.language,
  });
  const mobileTabs = [
    { value: "record" as const, label: t("tabs.record") },
    { value: "documents" as const, label: t("tabs.exams") },
    { value: "prescriptions" as const, label: t("tabs.prescriptions") },
    { value: "applications" as const, label: t("tabs.applications") },
    { value: "terms" as const, label: t("tabs.terms") },
  ];
  const mobileRouteByTab: Record<MedicalRecordPrimaryTab, string> = {
    record: "record",
    documents: "exams",
    prescriptions: "prescriptions",
    applications: "applications",
    terms: "terms",
  };

  const openMobileTranscription = () =>
    openTranscription({
      petId: pet.id,
      clinicId,
      appointmentId: appointmentId ?? null,
      petName: pet.name,
      appointmentDate: null,
      disableAutoRecovery: true,
      onApply: handleApplyTranscription,
      onMergeFields: (values) => {
        const normalized = normalizeStructuredData(values);
        delete normalized.consultation_date;
        formRef.current?.applyValues(normalized);
      },
    });

  const shouldShowTranscriptionBar =
    isTranscriptionOpen &&
    isTranscriptionMinimized &&
    transcriptionSession &&
    transcriptionState !== "closed" &&
    transcriptionState !== "draft" &&
    transcriptionState !== "ready";

  return (
    <PrescriptionDraftProvider context={draftContext}>
      <MedicalRecordMobileLayout
        shouldShowTranscriptionBar={shouldShowTranscriptionBar}
        header={
          <MobileMedicalRecordHeader
            title={t("mobile.titles.create")}
            subtitle={[pet.name, formatDateTimeBR(new Date().toISOString())].filter(Boolean).join(" · ")}
            backLabel={t("mobile.actions.back")}
            contextLabel={t("mobile.actions.openContext")}
            transcriptionLabel={t("mobile.actions.openTranscription")}
            moreLabel={t("mobile.actions.more")}
            showContext={mobileConfig.showContext}
            showTranscription={mobileConfig.showTranscription}
            showOverflow={mobileConfig.showOverflow}
            onBack={handleGoBackToPet}
            onOpenContext={() => setIsMobileContextOpen(true)}
            onOpenTranscription={openMobileTranscription}
          />
        }
        patient={<MobilePatientChip viewModel={mobilePatient} phoneLabel={t("mobile.actions.callGuardian")} editLabel={t("quickEdit.editPetAriaLabel")} onEdit={() => setIsMobileEditOpen(true)} />}
        tabs={
          <MobileMedicalRecordTabs
            activeTab={mobileActiveTab}
            tabs={mobileTabs}
            onTabChange={(tab) =>
              navigate(`/patients/pets/${pet.id}/medical-records/new/${mobileRouteByTab[tab]}`, {
                replace: true,
                state: location.state,
              })
            }
          />
        }
        recordingBar={
          shouldShowTranscriptionBar ? (
            <MobileRecordingBar
              state={transcriptionState}
              statusLabel={
                transcriptionState === "paused" || transcriptionState === "interrupted"
                  ? t("mobile.recordingBar.paused")
                  : t("mobile.recordingBar.recording")
              }
              durationLabel={formatDuration(recordingDuration)}
              processingLabel={
                transcriptionState === "analyzing"
                  ? t("mobile.recordingBar.analyzing")
                  : t("mobile.recordingBar.finalizing")
              }
              failedLabel={t("mobile.recordingBar.retry")}
              disconnectedLabel={t("mobile.recordingBar.disconnected")}
              openLabel={t("mobile.recordingBar.open")}
              pauseResumeLabel={recordingPhase === "paused" ? t("mobile.recordingBar.resume") : t("mobile.recordingBar.pause")}
              stopLabel={t("mobile.recordingBar.stop")}
              retryLabel={t("mobile.recordingBar.retry")}
              reconnectLabel={t("transcription.connection.reconnect")}
              onOpen={maximizeTranscription}
              onPauseResume={recordingPhase === "paused" ? resumeRecording : pauseRecording}
              onStop={stopRecording}
              onRetry={() => void retryAiExtraction()}
              onReconnect={reconnectTranscription}
            />
          ) : undefined
        }
        actions={
          <MobileMedicalRecordActionBar
            config={mobileConfig}
            secondaryLabel={t("actions.cancel")}
            primaryLabel={isSubmitting ? t("actions.saving") : t("mobile.actions.saveAttendance")}
            isSubmitting={isSubmitting}
            onSecondary={handleGoBackToPet}
            onPrimary={() => formRef.current?.submit()}
          />
        }
        overlays={
          <MobilePatientContextSheet
            open={isMobileContextOpen}
            onOpenChange={setIsMobileContextOpen}
            title={t("mobile.context.title")}
            description={t("mobile.context.description")}
            closeLabel={t("mobile.context.close")}
            records={medicalRecords ?? []}
            isLoadingRecords={isLoadingMedicalRecords}
            petId={pet.id}
            ownerId={pet.owner?.id ?? null}
            clinicId={clinicId}
          />
        }
      >
        {isMobile && activeTab === "novo" && clinicId ? (
          <MedicalRecordForm
            ref={formRef}
            formController={formController}
            onCancel={handleGoBackToPet}
            isLoading={isSubmitting}
            variant="mobile"
          />
        ) : null}
        {activeTab === "exames" ? (
          <MedicalRecordDocumentsSection
            variant="mobile"
            clinicId={clinicId}
            attachments={examAttachments}
            isLoadingAttachments={isLoadingAttachments}
            onUploadAttachments={handleUploadExamAttachments}
            onDeleteAttachment={deleteAttachment}
            onUpdateAttachment={updateAttachment}
          />
        ) : null}
        {activeTab === "prescricao" && clinicId ? (
          <PrescriptionComposer
            variant="mobile"
            petId={pet.id}
            clinicId={clinicId}
            appointmentId={appointmentId}
            medicalRecordId={undefined}
            speciesId={pet.species_id ?? undefined}
            onCreatedId={(createdId) =>
              setDraftPrescriptionIds((current) => current.includes(createdId) ? current : [...current, createdId])
            }
          />
        ) : null}
        {activeTab === "vacinas" && clinicId ? (
          <ApplicationTab
            variant="mobile"
            petId={pet.id}
            clinicId={clinicId}
            appointmentId={appointmentId}
            medicalRecordId={undefined}
            speciesId={pet.species_id ?? undefined}
            onDraftCreatedId={(createdId) =>
              setDraftVaccinationIds((current) => current.includes(createdId) ? current : [...current, createdId])
            }
            onDraftDeletedId={(deletedId) =>
              setDraftVaccinationIds((current) => current.filter((draftId) => draftId !== deletedId))
            }
          />
        ) : null}
        {activeTab === "termos" ? (
          <PatientTermsPanel
            variant="mobile"
            petId={pet.id}
            clinicId={clinicId}
            medicalRecordId={null}
            title={t("termsPanel.title")}
            description={t("termsPanel.description")}
            onDraftCreatedId={(createdId) =>
              setDraftTermIds((current) => current.includes(createdId) ? current : [...current, createdId])
            }
            onDraftDeletedId={(deletedId) =>
              setDraftTermIds((current) => current.filter((draftId) => draftId !== deletedId))
            }
          />
        ) : null}
      </MedicalRecordMobileLayout>
      <div className="hidden md:block space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleGoBackToPet}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
	          <div>
	            <h1 className="text-3xl font-bold text-foreground">{t("create.title")}</h1>
	            {(draftVaccinationIds.length > 0 || draftPrescriptionIds.length > 0 || draftTermIds.length > 0) && (
	              <p className="text-sm text-muted-foreground">
	                {[
	                  draftVaccinationIds.length > 0
	                    ? t("drafts.vaccines", { count: draftVaccinationIds.length })
	                    : null,
	                  draftPrescriptionIds.length > 0
	                    ? t("drafts.prescriptions", { count: draftPrescriptionIds.length })
	                    : null,
	                  draftTermIds.length > 0
	                    ? t("drafts.terms", { count: draftTermIds.length })
	                    : null,
	                ]
	                  .filter(Boolean)
	                  .join(" • ")}
	              </p>
	            )}
	          </div>
	        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto"
            disabled={!clinicId}
            onClick={() => void handleCopyRecordText()}
          >
            <Copy className="h-4 w-4 mr-2" />
            {t("actions.copyRecord")}
          </Button>
          <Button
            type="button"
            className="w-full sm:w-auto"
            onClick={() =>
              openTranscription({
                petId: pet.id,
                clinicId,
                appointmentId: appointmentId ?? null,
                petName: pet.name,
                appointmentDate: null,
                disableAutoRecovery: true,
                onApply: handleApplyTranscription,
                onMergeFields: (values) => {
                  const normalized = normalizeStructuredData(values);
                  delete normalized.consultation_date;
                  formRef.current?.applyValues(normalized);
                },
              })
            }
          >
            <Sparkles className="h-4 w-4" />
            {t("actions.transcribe")}
          </Button>
        </div>
      </div>
      <Card>
        <CardContent className="space-y-3 p-4 text-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-center gap-3 lg:max-w-[46%]">
              <Avatar className="h-14 w-14 shrink-0">
                {petPhoto ? (
                  <AvatarImage src={petPhoto} alt={pet.name} />
                ) : (
                  <AvatarFallback className="bg-muted/50 text-muted-foreground">
                    <CustomIcons name="paws" className="h-5 w-5 text-current" />
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="min-w-0 space-y-2">
                <p className="text-xs font-semibold uppercase text-muted-foreground">
                  {t("patientCard.title")}
                </p>
                <div className="flex min-w-0 items-center gap-2">
                  <Link
                    to={`/patients/pets/${pet.id}`}
                    className="truncate text-xl font-semibold text-primary hover:underline"
                  >
                    {pet.name}
                  </Link>
                  {isPetDeceased(pet) && pet.status?.name ? (
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {pet.status.name}
                    </Badge>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={() => setIsEditPetOpen(true)}
                    aria-label={t("quickEdit.editPetAriaLabel")}
                  >
                    <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </div>
                {petSummaryItems.length > 0 ? (
                  <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {petSummaryItems.map(({ key, Icon, label }) => (
                      <span key={key} className="flex min-w-0 items-center gap-1.5">
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{label}</span>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="min-w-0 space-y-2 lg:mr-10">
              <div className="flex min-w-0 items-start gap-2">
                <User className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.guardian")}</p>
                  {pet.owner?.id && pet.owner?.name ? (
                    <div className="flex min-w-0 items-center gap-1.5">
                      <Link
                        to={`/patients/owners/${pet.owner.id}`}
                        className="break-words font-semibold leading-snug text-primary hover:underline"
                      >
                        {pet.owner.name}
                      </Link>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => setIsEditOwnerOpen(true)}
                        aria-label={t("quickEdit.editOwnerAriaLabel")}
                      >
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">{t("patientCard.noGuardian")}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-10">
                <div className="flex min-w-0 items-center gap-2">
                  <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.phone")}</p>
                    {ownerPrimaryPhoneLabel ? (
                      ownerPrimaryPhone?.is_whatsapp ? (
                        <WhatsAppPhoneLink
                          phoneNumber={ownerPrimaryPhoneLabel}
                          className="min-w-0"
                          textClassName="truncate text-sm text-foreground hover:text-primary"
                        />
                      ) : (
                        <p className="truncate text-sm text-foreground">{ownerPrimaryPhoneLabel}</p>
                      )
                    ) : (
                      <p className="truncate text-sm text-muted-foreground">{t("patientCard.noPhone")}</p>
                    )}
                  </div>
                </div>
                <div className="flex min-w-0 items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.address")}</p>
                    {ownerPrimaryAddressLabel ? (
                      <AddressLink addressText={ownerPrimaryAddressLabel}>
                        <p className="truncate text-sm">{ownerPrimaryAddressLabel}</p>
                      </AddressLink>
                    ) : (
                      <p className="truncate text-sm text-muted-foreground">{t("patientCard.noAddress")}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>


          {(petAllergies.length > 0 || petTemperaments.length > 0 || pet.notes) ? (
            <div className="flex flex-col gap-2 border-t pt-3 md:flex-row md:items-start">
              {petAllergies.length > 0 ? (
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="shrink-0 text-xs font-semibold text-muted-foreground">{t("patientCard.allergies")}</span>
                  <div className="flex min-w-0 flex-wrap gap-1">
                    {petAllergies.map((item) => {
                      const name = item.condition?.name ?? item.name ?? "";
                      if (!name) return null;
                      return (
                        <Badge key={item.id} variant="default" className="max-w-full truncate">
                          {name}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {petTemperaments.length > 0 ? (
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="shrink-0 text-xs font-semibold text-muted-foreground">{t("patientCard.temperament")}</span>
                  <div className="flex min-w-0 flex-wrap gap-1">
                    {petTemperaments.map((item) => {
                      const name = item.condition?.name ?? item.name ?? "";
                      if (!name) return null;
                      return (
                        <Badge key={item.id} variant="secondary" className="max-w-full truncate">
                          {name}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {pet.notes ? (
                <div className="flex min-w-0 flex-[1.2] items-start gap-2">
                  <span className="shrink-0 text-xs font-semibold text-muted-foreground">{t("patientCard.patientNotes")}</span>
                  <TruncatedTextTooltip
                    text={pet.notes}
                    className="min-w-0 flex-1 truncate text-sm text-foreground"
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:gap-6 lg:grid-cols-[2fr,1fr]">
        <div className="min-w-0 space-y-4 order-2 lg:order-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">
                {t("clinicalRecordTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SegmentedTabs
                value={activeTab}
                variant="outline"
                onValueChange={(value) => {
                  const nextValue = value as "novo" | "exames" | "prescricao" | "vacinas" | "termos";
                  const nextRouteTab = Object.entries(ROUTE_TAB_TO_LEGACY).find(([, legacy]) => legacy === nextValue)?.[0] ?? "record";
                  navigate(`/patients/pets/${pet.id}/medical-records/new/${nextRouteTab}`, {
                    replace: true,
                    state: location.state,
                  });
                }}
                fill="screen"
                width="full"
                items={[
                  {
                    value: "novo",
                    label: t("tabs.record"),
                    content: (
                      <div className="pt-4">
                        {!isMobile && clinicId ? (
                          <MedicalRecordForm
                            ref={formRef}
                            formController={formController}
                            onCancel={handleGoBackToPet}
                            isLoading={isSubmitting}
                          />
                        ) : (
                          <Alert variant="danger">
                            <ShieldAlert className="h-4 w-4" />
                            <AlertTitle>{t("alerts.clinicUndefinedTitle")}</AlertTitle>
                            <AlertDescription>
                              {t("alerts.recordClinicUndefined")}
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    ),
                  },
                  {
                    value: "exames",
                    label: t("tabs.exams"),
                    content: (
                      <div className="pt-4 space-y-4">
                        {!clinicId ? (
                          <Alert variant="danger">
                            <ShieldAlert className="h-4 w-4" />
                            <AlertTitle>{t("alerts.clinicUndefinedTitle")}</AlertTitle>
                            <AlertDescription>
                              {t("alerts.examsClinicUndefined")}
                            </AlertDescription>
                          </Alert>
                        ) : (
                          <>
                            <MedicalRecordDocumentsSection
                              clinicId={clinicId}
                              attachments={examAttachments}
                              isLoadingAttachments={isLoadingAttachments}
                              onUploadAttachments={handleUploadExamAttachments}
                              onDeleteAttachment={deleteAttachment}
                              onUpdateAttachment={updateAttachment}
                            />
                          </>
                        )}
                      </div>
                    ),
                  },
                  {
                    value: "prescricao",
                    label: t("tabs.prescriptions"),
                    content: (
                      <div className="pt-4">
                        {clinicId ? (
                          <PrescriptionComposer
                            petId={pet.id}
                            clinicId={clinicId}
                            appointmentId={appointmentId}
                            medicalRecordId={undefined}
                            speciesId={pet.species_id ?? undefined}
                            onCreatedId={(createdId) => {
                              setDraftPrescriptionIds((current) =>
                                current.includes(createdId) ? current : [...current, createdId],
                              );
                            }}
                          />
                        ) : (
                          <Alert variant="danger">
                            <ShieldAlert className="h-4 w-4" />
                            <AlertTitle>{t("alerts.clinicUndefinedTitle")}</AlertTitle>
                            <AlertDescription>
                              {t("alerts.prescriptionsClinicUndefined")}
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    ),
                  },
                  {
                    value: "vacinas",
                    label: t("tabs.applications"),
                    content: (
                      <div className="pt-4">
                        {clinicId ? (
                          <ApplicationTab
                            petId={pet.id}
                            clinicId={clinicId}
                            appointmentId={appointmentId}
                            medicalRecordId={undefined}
                            speciesId={pet.species_id ?? undefined}
                            onDraftCreatedId={(createdId) => {
                              setDraftVaccinationIds((current) =>
                                current.includes(createdId) ? current : [...current, createdId],
                              );
                            }}
                            onDraftDeletedId={(deletedId) => {
                              setDraftVaccinationIds((current) =>
                                current.filter((id) => id !== deletedId),
                              );
                            }}
                          />
                        ) : (
                          <Alert variant="danger">
                            <ShieldAlert className="h-4 w-4" />
                            <AlertTitle>{t("alerts.clinicUndefinedTitle")}</AlertTitle>
                            <AlertDescription>
                              {t("alerts.applicationsClinicUndefined")}
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    ),
                  },
                  {
                    value: "termos",
                    label: t("tabs.terms"),
                    content: (
                      <div className="pt-4">
                        <PatientTermsPanel
                          petId={pet.id}
                          clinicId={clinicId}
                          medicalRecordId={null}
                          title={t("termsPanel.title")}
                          description={t("termsPanel.description")}
                          onDraftCreatedId={(createdId) => {
                            setDraftTermIds((current) =>
                              current.includes(createdId) ? current : [...current, createdId],
                            );
                          }}
                          onDraftDeletedId={(deletedId) => {
                            setDraftTermIds((current) => current.filter((id) => id !== deletedId));
                          }}
                        />
                      </div>
                    ),
                  },
                ]}
              />
            </CardContent>
          </Card>
          <div className="flex flex-col !mb-16 gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="outline"
              className="shadow-soft-search"
              onClick={handleGoBackToPet}
            >
              <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              className="w-full sm:w-auto"
              disabled={isSubmitting}
              onClick={() => formRef.current?.submit()}
            >
              <CustomIcons name="check-circle" className="!h-6 !w-6 !text-primary-foreground" />
              <span className="font-normal">
                {isSubmitting ? t("actions.saving") : t("save", { ns: "common" })}
              </span>
            </Button>
          </div>
        </div>
        <div className="min-w-0 space-y-4 order-1 lg:order-2">
          {activeTab === "prescricao" && clinicId ? (
            <PrescriptionDraftPreviewCard className="hidden lg:flex" />
          ) : null}
          {activeTab !== "prescricao" ? (
            <>
              <RecentConsultationsCard
                records={medicalRecords ?? []}
                isLoading={isLoadingMedicalRecords}
                maxItems={2}
              />

              <AttendanceCarePanel
                petId={pet.id}
                ownerId={pet.owner?.id ?? null}
                clinicId={clinicId}
              />
            </>
          ) : null}
        </div>
      </div>

      {returnVaccineConflictContext ? (
        <ReturnApplicationConflictDialog
          open={returnVaccineConflictOpen}
          onOpenChange={(open) => {
            if (!open) {
              resolveReturnVaccineConflict(null);
              return;
            }
            setReturnVaccineConflictOpen(true);
          }}
          followUpDate={returnVaccineConflictContext.followUpDate}
          vaccineAppointments={returnVaccineConflictContext.vaccineAppointments}
          value={returnVaccineConflictChoice}
          onValueChange={setReturnVaccineConflictChoice}
          onConfirm={() => resolveReturnVaccineConflict(returnVaccineConflictChoice)}
        />
      ) : null}

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={isExitDialogOpen} onOpenChange={setIsExitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("exitDialog.createTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("exitDialog.createDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelExit}>
              {t("actions.continueEditing")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmExit}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("actions.discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ProfileDataModal
        open={showProfileDataModal}
        onOpenChange={(open) => {
          setShowProfileDataModal(open);
          if (!open) {
            setPendingMedicalRecord(null);
          }
        }}
        needsCRMV
        needsMAPA={false}
        needsAddress={false}
        onComplete={handleProfileDataComplete}
        contextLabel="salvar o prontuário"
      />

      <QuickEditPetDialog
        petId={pet.id}
        open={isEditPetOpen}
        onOpenChange={setIsEditPetOpen}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["pet", pet.id] });
        }}
      />

      {pet.owner?.id ? (
        <QuickEditOwnerDialog
          ownerId={pet.owner.id}
          open={isEditOwnerOpen}
          onOpenChange={setIsEditOwnerOpen}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ["pet", pet.id] });
          }}
        />
      ) : null}

      <MobilePatientEditFlow
        petId={pet.id}
        ownerId={pet.owner?.id}
        open={isMobileEditOpen}
        onOpenChange={setIsMobileEditOpen}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["pet", pet.id] });
        }}
      />
      </div>
    </PrescriptionDraftProvider>
  );
};

export default CreateMedicalRecord;
